/**
 * TagsPage
 * Main page for browsing and managing project tags
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Tag as TagIcon } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { Button, ConfirmModal } from '../../components/ui';
import { tagsApi } from '../../services/api';
import type { Tag, TagColor, CreateTagRequest, UpdateTagRequest } from '../../types';
import styles from './TagsPage.module.css';

const TAG_COLORS: TagColor[] = ['blue', 'green', 'red', 'yellow', 'purple', 'orange', 'pink', 'gray', 'teal'];

const COLOR_CLASS_MAP: Record<TagColor, string> = {
  blue: styles.colorBlue,
  green: styles.colorGreen,
  red: styles.colorRed,
  yellow: styles.colorYellow,
  purple: styles.colorPurple,
  orange: styles.colorOrange,
  pink: styles.colorPink,
  gray: styles.colorGray,
  teal: styles.colorTeal,
};

export const TagsPage: React.FC = () => {
  // Data state
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [colorFilter, setColorFilter] = useState<string>('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState<TagColor>('blue');
  const [isSaving, setIsSaving] = useState(false);

  // Load tags
  const loadTags = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await tagsApi.list({
        search: searchQuery || undefined,
        color: colorFilter || undefined,
      });
      if (response.success && response.data) {
        setTags(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Failed to load tags:', err);
      setError('Failed to load tags. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, colorFilter]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // Open create modal
  const handleCreate = () => {
    setEditingTag(null);
    setFormName('');
    setFormDescription('');
    setFormColor('blue');
    setIsModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormDescription(tag.description || '');
    setFormColor(tag.color);
    setIsModalOpen(true);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formName.trim()) return;
    setIsSaving(true);
    try {
      if (editingTag) {
        const data: UpdateTagRequest = { name: formName, description: formDescription, color: formColor };
        await tagsApi.update(editingTag.id, data);
      } else {
        const data: CreateTagRequest = { name: formName, description: formDescription, color: formColor };
        await tagsApi.create(data);
      }
      setIsModalOpen(false);
      await loadTags();
    } catch (err) {
      console.error('Failed to save tag:', err);
      setError('Failed to save tag. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete
  const confirmDelete = async () => {
    if (!tagToDelete) return;
    try {
      await tagsApi.delete(tagToDelete.id);
      setTagToDelete(null);
      await loadTags();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      setError('Failed to delete tag. Please try again.');
    }
  };

  return (
    <AppLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.count}>
            {tags.length} {tags.length === 1 ? 'TAG' : 'TAGS'}
          </h2>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus size={16} />}
            onClick={handleCreate}
          >
            Create Tag
          </Button>
        </div>

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className={styles.colorFilter}
            value={colorFilter}
            onChange={(e) => setColorFilter(e.target.value)}
          >
            <option value="">All Colors</option>
            {TAG_COLORS.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {isLoading ? (
          <div className={styles.loading}>Loading tags...</div>
        ) : tags.length === 0 ? (
          <div className={styles.empty}>
            <TagIcon size={48} />
            <span>No tags found. Create your first tag!</span>
          </div>
        ) : (
          <div className={styles.grid}>
            {tags.map((tag) => (
              <div key={tag.id} className={styles.tagCard} onClick={() => handleEdit(tag)}>
                <div className={styles.tagCardHeader}>
                  <h3 className={styles.tagName}>{tag.name}</h3>
                  <div className={styles.tagActions}>
                    <button
                      className={styles.iconButton}
                      onClick={(e) => { e.stopPropagation(); handleEdit(tag); }}
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                      onClick={(e) => { e.stopPropagation(); setTagToDelete(tag); }}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {tag.description && (
                  <p className={styles.tagDescription}>{tag.description}</p>
                )}
                <div className={styles.tagMeta}>
                  <span className={`${styles.colorDot} ${COLOR_CLASS_MAP[tag.color] || styles.colorBlue}`} />
                  <span className={styles.usageCount}>
                    {tag.usageCount} {tag.usageCount === 1 ? 'resource' : 'resources'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className={styles.modal}>
          <div className={styles.modalBackdrop} onClick={() => setIsModalOpen(false)} />
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>
              {editingTag ? 'Edit Tag' : 'Create Tag'}
            </h3>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Name *</label>
              <input
                type="text"
                className={styles.formInput}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter tag name"
                autoFocus
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Description</label>
              <textarea
                className={styles.formTextarea}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Color</label>
              <div className={styles.colorPicker}>
                {TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`${styles.colorOption} ${COLOR_CLASS_MAP[c]} ${formColor === c ? styles.colorOptionSelected : ''}`}
                    onClick={() => setFormColor(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!formName.trim() || isSaving}
              >
                {isSaving ? 'Saving...' : editingTag ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {tagToDelete && (
        <ConfirmModal
          isOpen={true}
          title="Delete Tag"
          message={`Are you sure you want to delete "${tagToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onClose={() => setTagToDelete(null)}
          variant="danger"
        />
      )}
    </AppLayout>
  );
};

export default TagsPage;
